import React, { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Unstable_Grid2';
import { useSnackbar } from 'notistack';
import TextInputComponent from '../../../../components/input/TextInputComponent';
import CertificatePicker from './CertificatePicker';

const parseRuleForCertIds = (ruleCode) => {
  if (!ruleCode) return [];
  const regex = /ProductCatalogueId\s*==\s*(\d+)/g;
  const ids = new Set();
  let match;
  while ((match = regex.exec(ruleCode)) !== null) {
    ids.add(parseInt(match[1], 10));
  }
  return Array.from(ids);
};

const ruleHasExpiryCheck = (ruleCode) => {
  if (!ruleCode) return false;
  return /ExpiryDate\s*>\s*DateOnly/i.test(ruleCode);
};

const ruleHasIsActiveCheck = (ruleCode) => {
  if (!ruleCode) return false;
  return /c\.IsActive/i.test(ruleCode);
};

const generateRuleCode = (certIds, includeExpiry, includeIsActive) => {
  if (!certIds || certIds.length === 0) return '';
  const conditions = certIds.map((id) => `c.ProductCatalogueId == ${id}`).join(' || ');
  const extra = [];
  if (includeExpiry) extra.push('c.ExpiryDate > DateOnly.FromDateTime(DateTime.UtcNow)');
  if (includeIsActive) extra.push('c.IsActive');
  const suffix = extra.length > 0 ? ` && ${extra.join(' && ')}` : '';
  return `certificates.Any(c => (${conditions})${suffix})`;
};

export { parseRuleForCertIds, ruleHasExpiryCheck, ruleHasIsActiveCheck, generateRuleCode };

export default function AccessRuleBuilder({
  certificates,
  loadingCertificates,
  teams,
  editingRuleId,
  editingRuleTeamId,
  onCancelEdit,
  onSave,
  onVerify,
  onCancel,
  loading,
  verifying,
  getCertNameById,
  initialRuleName,
  initialRuleCode,
  initialCertIds,
  initialIncludeExpiry,
  initialIncludeIsActive,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [applyTargetTeamIds, setApplyTargetTeamIds] = useState([]);
  const [ruleName, setRuleName] = useState(initialRuleName || '');
  const [ruleCode, setRuleCode] = useState(initialRuleCode || '');
  const [selectedCertIds, setSelectedCertIds] = useState(initialCertIds || []);
  const [includeExpiryCheck, setIncludeExpiryCheck] = useState(initialIncludeExpiry ?? true);
  const [includeIsActiveCheck, setIncludeIsActiveCheck] = useState(initialIncludeIsActive ?? true);
  const [certSearchFilter, setCertSearchFilter] = useState('');
  const [customCatalogueId, setCustomCatalogueId] = useState('');
  const [validated, setValidated] = useState(false);
  const [isRuleVerified, setIsRuleVerified] = useState(false);
  const [showRawCode, setShowRawCode] = useState(false);

  React.useEffect(() => {
    setRuleName(initialRuleName || '');
    setRuleCode(initialRuleCode || '');
    setSelectedCertIds(initialCertIds || []);
    setIncludeExpiryCheck(initialIncludeExpiry ?? true);
    setIncludeIsActiveCheck(initialIncludeIsActive ?? true);
    setIsRuleVerified(false);
    setValidated(false);
    setCertSearchFilter('');
    setCustomCatalogueId('');
  }, [editingRuleId, initialRuleName, initialRuleCode]);

  React.useEffect(() => {
    setIsRuleVerified(false);
  }, [ruleCode]);

  useEffect(() => {
    setApplyTargetTeamIds((prev) => {
      const valid = new Set(teams.map((t) => String(t.id)));
      const next = prev.filter((id) => valid.has(String(id)));
      if (next.length >= 1) return next;
      return [];
    });
  }, [teams]);

  useEffect(() => {
    if (editingRuleId && editingRuleTeamId != null) {
      setApplyTargetTeamIds([editingRuleTeamId]);
      return;
    }
    if (editingRuleId) return;
    setApplyTargetTeamIds((prev) => {
      if (prev.length > 0) return prev;
      if (teams.length === 0) return [];
      return [teams[0].id];
    });
  }, [editingRuleId, editingRuleTeamId, teams]);

  const toggleApplyTargetTeam = (teamId) => {
    setApplyTargetTeamIds((prev) => {
      const idStr = String(teamId);
      const has = prev.some((x) => String(x) === idStr);
      if (has && prev.length === 1) {
        enqueueSnackbar('Select at least one team to apply this rule to', { variant: 'warning' });
        return prev;
      }
      if (has) return prev.filter((x) => String(x) !== idStr);
      return [...prev, teamId];
    });
  };

  const selectAllApplyTargets = () => {
    setApplyTargetTeamIds(teams.map((t) => t.id));
  };

  const applyTargetsFirstTeamOnly = () => {
    if (teams.length > 0) setApplyTargetTeamIds([teams[0].id]);
  };

  const handleToggleCert = (productCatalogueId) => {
    setSelectedCertIds((prev) => {
      const next = prev.includes(productCatalogueId)
        ? prev.filter((id) => id !== productCatalogueId)
        : [...prev, productCatalogueId];
      setRuleCode(generateRuleCode(next, includeExpiryCheck, includeIsActiveCheck));
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = certificates.map((c) => c.productCatalogueId);
    setSelectedCertIds(allIds);
    setRuleCode(generateRuleCode(allIds, includeExpiryCheck, includeIsActiveCheck));
  };

  const handleDeselectAll = () => {
    setSelectedCertIds([]);
    setRuleCode('');
  };

  const handleExpiryToggle = (checked) => {
    setIncludeExpiryCheck(checked);
    if (selectedCertIds.length > 0) {
      setRuleCode(generateRuleCode(selectedCertIds, checked, includeIsActiveCheck));
    }
  };

  const handleIsActiveToggle = (checked) => {
    setIncludeIsActiveCheck(checked);
    if (selectedCertIds.length > 0) {
      setRuleCode(generateRuleCode(selectedCertIds, includeExpiryCheck, checked));
    }
  };

  const handleAddCustomId = () => {
    const id = parseInt(customCatalogueId, 10);
    if (isNaN(id) || id <= 0) return;
    if (selectedCertIds.includes(id)) {
      setCustomCatalogueId('');
      return;
    }
    const next = [...selectedCertIds, id];
    setSelectedCertIds(next);
    setRuleCode(generateRuleCode(next, includeExpiryCheck, includeIsActiveCheck));
    setCustomCatalogueId('');
  };

  const handleVerify = async () => {
    if (!ruleCode.trim()) return;
    const result = await onVerify(ruleCode);
    setIsRuleVerified(result);
  };

  const handleSave = () => {
    setValidated(true);
    if (!ruleName || !ruleCode) return;
    if (applyTargetTeamIds.length === 0) {
      enqueueSnackbar('Select at least one team to apply this rule to', { variant: 'warning' });
      return;
    }
    onSave({
      ruleName,
      ruleCode,
      editingRuleId,
      targetTeamIds: applyTargetTeamIds,
    });
  };

  const handleCancelEdit = () => {
    setRuleName('');
    setRuleCode('');
    setSelectedCertIds([]);
    setCertSearchFilter('');
    setIncludeExpiryCheck(true);
    setIncludeIsActiveCheck(true);
    setCustomCatalogueId('');
    setIsRuleVerified(false);
    setValidated(false);
    onCancelEdit();
  };

  const resetAfterSave = useCallback(() => {
    setValidated(false);
    setRuleName('');
    setRuleCode('');
    setSelectedCertIds([]);
    setCertSearchFilter('');
    setIncludeExpiryCheck(true);
    setIncludeIsActiveCheck(true);
    setCustomCatalogueId('');
    setIsRuleVerified(false);
  }, []);

  React.useImperativeHandle(
    React.useRef(null),
    () => ({ resetAfterSave }),
    [resetAfterSave]
  );

  return (
    <Card>
      <CardHeader
        title={editingRuleId ? 'Edit Access Rule' : 'Create Access Rule'}
        action={
          editingRuleId && (
            <Button size="small" onClick={handleCancelEdit}>
              Cancel Edit
            </Button>
          )
        }
      />
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3} direction="column">
          {teams.length > 0 && (
            <Grid xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {editingRuleId ? 'Teams this rule applies to' : 'Apply new rule to these teams'}
              </Typography>
              {!editingRuleId && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Check every team that should use this rule. You can change teams when editing too.
                </Typography>
              )}
              {editingRuleId && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Checked teams get this rule. Keep the current team checked to update it; add teams
                  for copies; remove it to drop that row and assign only to who remains checked.
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={selectAllApplyTargets}
                  disabled={loading || verifying || teams.length <= 1}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={applyTargetsFirstTeamOnly}
                  disabled={loading || verifying || teams.length === 0}
                >
                  First team only
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, columnGap: 2 }}>
                {teams.map((t) => (
                  <FormControlLabel
                    key={t.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={applyTargetTeamIds.some((id) => String(id) === String(t.id))}
                        onChange={() => toggleApplyTargetTeam(t.id)}
                        disabled={loading || verifying}
                      />
                    }
                    label={t.name}
                  />
                ))}
              </Box>
            </Grid>
          )}

          <Grid xs={12}>
            <TextInputComponent
              value={ruleName}
              setValue={setRuleName}
              label="Rule Name"
              error={validated && !ruleName}
              disabled={loading || verifying}
              helperText={validated && !ruleName ? 'Required' : ''}
            />
          </Grid>

          <Grid xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Required Certificates
            </Typography>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Select the certificate types that must be held for site induction. The rule code is generated automatically.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeExpiryCheck}
                    onChange={(e) => handleExpiryToggle(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Must not be expired</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeIsActiveCheck}
                    onChange={(e) => handleIsActiveToggle(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Must be active</Typography>}
              />
            </Box>

            <CertificatePicker
              certificates={certificates}
              selectedCertIds={selectedCertIds}
              onToggleCert={handleToggleCert}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              loading={loadingCertificates}
              searchFilter={certSearchFilter}
              onSearchFilterChange={setCertSearchFilter}
              customIdValue={customCatalogueId}
              onCustomIdChange={setCustomCatalogueId}
              onAddCustomId={handleAddCustomId}
              getCertNameById={getCertNameById}
              emptyMessage="No certificates synced from Skillko for this account yet." 
            />
          </Grid>

          <Grid xs={12}>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowRawCode(!showRawCode)}
              sx={{ textTransform: 'none', mb: 1 }}
            >
              {showRawCode ? 'Hide rule code' : 'Advanced: View / edit rule code'}
            </Button>
            <Collapse in={showRawCode}>
              <Box sx={{ position: 'relative' }}>
                <TextInputComponent
                  value={ruleCode}
                  setValue={setRuleCode}
                  label="Rule Code (C# expression)"
                  multiline
                  rows={6}
                  error={validated && !ruleCode}
                  disabled={loading || verifying}
                />
                {isRuleVerified && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 10,
                      top: 10,
                      bgcolor: 'success.light',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}
                  >
                    Verified
                  </Box>
                )}
              </Box>
            </Collapse>
            {!showRawCode && ruleCode && (
              <Typography variant="caption" color="text.secondary">
                Rule code generated ({ruleCode.length} chars)
              </Typography>
            )}
          </Grid>

          <Grid xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
              <Button onClick={onCancel} disabled={loading || verifying} variant="outlined">
                Cancel
              </Button>
              <LoadingButton
                onClick={handleVerify}
                loading={verifying}
                variant="outlined"
                color="info"
                disabled={loading || !ruleCode}
              >
                Verify Rule
              </LoadingButton>
              <LoadingButton
                onClick={handleSave}
                loading={loading}
                variant="contained"
                color="primary"
                disabled={teams.length === 0 || loading || verifying}
              >
                {editingRuleId ? 'Update Rule' : 'Create Rule'}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
}
